import * as React from "react";
import { Pause, Play, RotateCcw, RotateCw, ZoomIn, ZoomOut } from "lucide-react";
import { VIEWER_MODELS } from "../data/viewerModels";
export function ModelViewer({ model, image }) {
  var k;
  var m;
  var p;
  var y;
  var b;
  const n = VIEWER_MODELS[model];
  const r = React.useRef(null);
  const i = React.useRef(null);
  const [o, s] = React.useState("loading");
  const [l, c] = React.useState(0);
  const [u, f] = React.useState(false);
  const [d, h] = React.useState("solid");
  const [g, x] = React.useState(
    ((m = (k = n.zones) == null ? undefined : k[0]) == null ? undefined : m.id) || "",
  );
  React.useEffect(() => {
    const j = r.current;
    if (!j) {
      return;
    }
    const S = new AbortController();
    let P = false;
    s("loading");
    const C = new IntersectionObserver(
      (N) => {
        if (!P && !!N.some((A) => A.isIntersecting)) {
          P = true;
          C.disconnect();
          import("../lib/atlasViewerEngine.js")
            .then(async ({ mountAtlas }) => {
              if (S.signal.aborted) {
                return;
              }
              const z = await mountAtlas(
                j,
                S.signal,
                () => {
                  if (!S.signal.aborted) {
                    s("error");
                    f(false);
                  }
                },
                n,
              );
              if (S.signal.aborted) {
                if (z != null) {
                  z.dispose();
                }
                return;
              }
              i.current = z;
              s(z ? "ready" : "error");
            })
            .catch(() => {
              if (!S.signal.aborted) {
                s("error");
              }
            });
        }
      },
      {
        rootMargin: "200px",
      },
    );
    C.observe(j);
    return () => {
      var N;
      C.disconnect();
      S.abort();
      if ((N = i.current) != null) {
        N.dispose();
      }
      i.current = null;
    };
  }, [n, l]);
  React.useEffect(() => {
    var j;
    if (o === "ready") {
      if ((j = i.current) != null) {
        j.style(d, g);
      }
    }
  }, [o, d, g]);
  const w = o === "ready";
  return (
    <section id="product-model" className="catalog-wrap model-explorer">
      <p className="catalog-kicker">{n.label} / In three dimensions</p>
      <h2>Explore the system.</h2>
      <div className="model-stage">
        <div className="model-canvas" ref={r} data-model-status={o} />
        {!w && (
          <div className="model-placeholder">
            <img src={image} alt={`${n.label} concept preview`} />
            <p role="status">{o === "error" ? "3D preview unavailable." : "Loading 3D preview..."}</p>
            {o === "error" && <button onClick={() => c((j) => j + 1)}>Retry</button>}
          </div>
        )}
      </div>
      <div className="model-toolbar" aria-label="3D controls">
        {["home", "front", "side", "back"].map((j) => (
          <button
            disabled={!w}
            onClick={() => {
              var S;
              if ((S = i.current) == null) {
                return undefined;
              } else {
                return S.view(j);
              }
            }}
            key={j}
          >
            {j === "home"
              ? "Perspective"
              : j === "back" && n.studio
                ? "Top"
                : j[0].toUpperCase() + j.slice(1)}
          </button>
        ))}
        <span className="model-divider" />
        <button
          disabled={!w}
          aria-label="Rotate left"
          title="Rotate left"
          onClick={() => {
            var j;
            if ((j = i.current) == null) {
              return undefined;
            } else {
              return j.rotate(-0.25);
            }
          }}
        >
          <RotateCcw size={20} />
        </button>
        <button
          disabled={!w}
          aria-label="Rotate right"
          title="Rotate right"
          onClick={() => {
            var j;
            if ((j = i.current) == null) {
              return undefined;
            } else {
              return j.rotate(0.25);
            }
          }}
        >
          <RotateCw size={20} />
        </button>
        <button
          disabled={!w}
          aria-label="Zoom in"
          title="Zoom in"
          onClick={() => {
            var j;
            if ((j = i.current) == null) {
              return undefined;
            } else {
              return j.zoom(0.85);
            }
          }}
        >
          <ZoomIn size={20} />
        </button>
        <button
          disabled={!w}
          aria-label="Zoom out"
          title="Zoom out"
          onClick={() => {
            var j;
            if ((j = i.current) == null) {
              return undefined;
            } else {
              return j.zoom(1.15);
            }
          }}
        >
          <ZoomOut size={20} />
        </button>
        <button
          disabled={!w}
          aria-label={u ? "Pause rotation" : "Auto rotate"}
          title={u ? "Pause rotation" : "Auto rotate"}
          aria-pressed={u}
          onClick={() => {
            var j;
            if ((j = i.current) != null) {
              j.spin(!u);
            }
            f(!u);
          }}
        >
          {u ? <Pause size={20} /> : <Play size={20} />}
        </button>
      </div>
      {n.lineArt && (
        <div className="model-toolbar" aria-label="Render style">
          {["solid", "line", ...(n.zones ? ["zones"] : [])].map((j) => (
            <button disabled={!w} aria-pressed={d === j} onClick={() => h(j)} key={j}>
              {j === "solid" ? "Exterior" : j === "line" ? "Line study" : "Function map"}
            </button>
          ))}
          {d === "zones" &&
            ((p = n.zones) == null
              ? undefined
              : p.map((j) => (
                  <button aria-pressed={g === j.id} onClick={() => x(j.id)} key={j.id}>
                    {j.label}
                  </button>
                )))}
        </div>
      )}
      {d === "zones" && (
        <p>
          {(b = (y = n.zones) == null ? undefined : y.find((j) => j.id === g)) == null
            ? undefined
            : b.description}
        </p>
      )}
      <p className="catalog-note">
        Conceptual exterior and layout only, not engineering CAD. Internal structures, materials and
        dimensions require project-specific confirmation.
      </p>
    </section>
  );
}
